// Sidebar.jsx
import React, { useEffect, useState, useMemo } from "react";
import { Link, useLocation, useParams, useNavigate } from "react-router-dom";
import { Image } from "react-bootstrap";
import "./index.css";
import { SidebarContent } from "./SidebarContent";
import { routes } from "../routes/pageRoutes";
import { useDispatch, useSelector } from "react-redux";
import {
  RecordIcon, LogoutIcon,
  // DashboardSquare02Icon 
} from "../assets/icons";
import { fetchMapLocations, resetMapNavigation } from "../redux/slice/mapSlice";
import { fetchJantriLocations, resetJantriNavigation } from "../redux/slice/jantriSlice";
import { usePermission } from "../hooks/usePermission";
import { useLoader } from "../context/LoaderContext";
import useMounted from "../hooks/useMounted";
import { Collapse, Button, OverlayTrigger, Popover } from 'react-bootstrap';
import ConfirmModal from "../components/withModal/ConfirmModal";
import {
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
// Pre-compute static routes set for O(1) matching checks
const staticRoutesSet = new Set(
  Object.values(routes).filter((r) => !r.includes(":"))
);

const isStaticRoute = (path) => {
  return staticRoutesSet.has(path);
};

// Cache regex objects for parameter routing
const regexCache = {};

// Helper: check if a path matches current location
const matchPath = (locationPath, testPath) => {
  if (!testPath) return false;

  // normalize (remove trailing slashes; keep root as '/')
  const norm = p => (p.replace(/\/+$/, '') || '/');
  const loc = norm(locationPath);
  const test = norm(testPath);

  // exact for root
  if (test === '/') return loc === '/';

  // handle param routes like /users/:id
  if (test.includes(':')) {
    // If the location is a known static route, it should not match a parameter fallback route
    if (isStaticRoute(loc)) {
      return false;
    }
    if (!regexCache[test]) {
      regexCache[test] = new RegExp('^' + test.replace(/:[^/]+/g, '[^/]+') + '$');
    }
    return regexCache[test].test(loc);
  }

  // exact or path-boundary prefix (/npa matches /npa/..., but not /npaX)
  if (loc === test) return true;
  if (isStaticRoute(loc)) return false;
  return loc.startsWith(test + '/');
};

// Helper: determine if a specific item matches the current route conditionals
const isItemActive = (node, locationPath, fromPath, userDetail) => {
  // Specially handle routes.viewUser and routes.viewAllProducts for R-Point vs MEP Users
  if (matchPath(locationPath, routes.viewUser) || matchPath(locationPath, routes.viewAllProducts)) {
    const isMep = (fromPath === routes.userMep) || (userDetail?.is_migrated === true || userDetail?.is_migrated === 1);
    if (node.link === routes.userMep) {
      return isMep;
    }
    if (node.link === routes.userRPoint) {
      return !isMep;
    }
  }

  // Standard match
  if (node.link && matchPath(locationPath, node.link)) return true;
  if (Array.isArray(node.matchRoutes)) {
    if (node.matchRoutes.some(route => matchPath(locationPath, route))) return true;
  }
  return false;
};

// Helper: determine active link anywhere in a nested tree
const treeHasActiveLink = (node, locationPath, fromPath, userDetail) => {
  if (isItemActive(node, locationPath, fromPath, userDetail)) return true;
  if (Array.isArray(node.children)) {
    return node.children.some(child => treeHasActiveLink(child, locationPath, fromPath, userDetail));
  }
  return false;
};

// Helper: find the path of titles to the active leaf
const findActivePath = (nodes, locationPath, fromPath, userDetail, currentPath = []) => {
  for (const node of nodes) {
    if (treeHasActiveLink(node, locationPath, fromPath, userDetail)) {
      if (node.children && node.children.length > 0) {
        const childPath = findActivePath(node.children, locationPath, fromPath, userDetail, [...currentPath, node.title]);
        if (childPath) return childPath;
        return [...currentPath, node.title];
      }
      return currentPath;
    }
  }
  return null;
};

// Optimized component specifically for Map and Jantri links that use Redux selectors
function MapJantriLeafNode({ node, padLeft, active, isCollapsed, level }) {
  const dispatch = useDispatch();
  const mapTypes = useSelector((state) => state.maps.list);
  const jantriTypes = useSelector((state) => state.jantri.list);
  const jantriNavStack = useSelector((state) => state.jantri.navStack);
  const mapNavStack = useSelector((state) => state.maps.navStack);
  const searchParams = useParams();

  const handleClick = () => {
    if (node.link?.startsWith("/map")) {
      if (!mapNavStack || mapNavStack.length === 0) {
        return;
      }
      dispatch(resetMapNavigation());

      const mapType = mapTypes.find(
        (m) => m.code.toLowerCase() === searchParams.mapCode?.toLowerCase()
      );

      if (!mapType?.id) {
        console.warn("Map type not resolved, skipping API call");
        return;
      }

      dispatch(
        fetchMapLocations({
          map_type_id: mapType.id,
          parent_id: null,
          page: 1,
          limit: 10,
          search: null,
          direction: "forward",
          mode: "sidebar_click",
        })
      );
    }
    if (node.link?.startsWith("/jantri")) {
      if (!jantriNavStack || jantriNavStack.length === 0) {
        return;
      }
      dispatch(resetJantriNavigation());
      const jantriType = jantriTypes.find(
        (m) => m.code.toLowerCase() === searchParams.type?.toLowerCase()
      );

      if (!jantriType?.id) {
        console.warn("Map type not resolved, skipping API call");
        return;
      }

      dispatch(
        fetchJantriLocations({
          jantri_type_id: jantriType.id,
          parent_id: null,
          page: 1,
          limit: 10,
          search: null,
          direction: "forward",
          mode: "sidebar_click",
        })
      );
    }
  };


  return (
    <li className="sidebar-item">
      <Link
        to={node.link || "#"}
        onClick={handleClick}
        className={`sidebar-link ${active ? "active" : ""} ${level === 0 ? "level-0" : ""}`}
        style={{ paddingLeft: isCollapsed ? "12px" : padLeft }}
      >

        {node.icon && <span className="sidebar-icon-wrapper d-inline-flex">{node.icon}</span>}
        {!isCollapsed && <span className="sidebar-title-mr text-truncate">{node.title}</span>}
      </Link>
    </li>
  );
}

// Lightweight standard leaf node
function StandardLeafNode({ node, padLeft, active, isCollapsed, level }) {
  const handleClick = () => {
    if (node.link?.startsWith("/broker-crm/properties")) {
      sessionStorage.removeItem("allPublicProperties_searchQuery");
      sessionStorage.removeItem("allPublicProperties_filterValues");
      sessionStorage.removeItem("allPublicProperties_limit");
      sessionStorage.removeItem("allPublicProperties_page");
      sessionStorage.removeItem("allPublicProperties_cardStatus");
    }
  };


  return (
    <li className="sidebar-item">
      <Link
        to={node.link || "#"}
        onClick={handleClick}
        className={`sidebar-link ${active ? "active" : ""} ${level === 0 ? "level-0" : ""}`}
        style={{ paddingLeft: isCollapsed ? "12px" : padLeft }}
      >
        {node.icon && <span className="sidebar-icon-wrapper d-inline-flex">{node.icon}</span>}
        {!isCollapsed && <span className="sidebar-title-mr text-truncate">{node.title}</span>}
      </Link>
    </li>
  );
}

function SidebarNode({ node, level, locationPath, isOpen, onToggle, openPath, userDetail, fromPath, isCollapsed }) {
  const isLeaf = !node.children || node.children.length === 0;
  // Spacing by level
  const padLeft = 12 + level * 14;

  if (isLeaf) {
    const active = isItemActive(node, locationPath, fromPath, userDetail);
    const isMapOrJantri = node.link?.startsWith("/map") || node.link?.startsWith("/jantri");

    if (isMapOrJantri) {
      return (
        <MapJantriLeafNode
          node={node}
          padLeft={padLeft}
          active={active}
          isCollapsed={isCollapsed}
          level={level}
        />
      );
    }

    return (
      <StandardLeafNode
        node={node}
        padLeft={padLeft}
        active={active}
        isCollapsed={isCollapsed}
        level={level}
      />
    );
  }

  const isActive = treeHasActiveLink(node, locationPath, fromPath, userDetail);

  // If collapsed and we are at the top level, show submenu in a Popover overlay
  if (isCollapsed && level === 0) {
    const popoverContent = (nodes) => {
      return nodes.map((child, idx) => {
        if (child.children && child.children.length > 0) {
          return (
            <div key={idx} className="mb-2">
              <div className="text-muted px-2 py-1 fs-xs fw-semibold text-uppercase popover-subgroup-title" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>
                {child.title}
              </div>
              <ul className="list-unstyled mb-0 ps-2">
                {popoverContent(child.children)}
              </ul>
            </div>
          );
        }

        const isActiveChild = isItemActive(child, locationPath, fromPath, userDetail);
        return (
          <li key={idx} className="sidebar-popover-item mb-1">
            <Link
              to={child.link || "#"}
              className={`sidebar-popover-link d-flex align-items-center justify-content-between p-2 rounded text-decoration-none ${isActiveChild ? "active" : ""
                }`}
            >
              <span style={{ fontSize: '13px' }}>{child.title}</span>
            </Link>
          </li>
        );
      });
    };

    const popover = (
      <Popover id={`popover-${node.title}`} className="sidebar-popover border shadow-sm">
        <Popover.Header as="h3" className="popover-header-title text-capitalize border-bottom bg-light px-3 py-2 fw-semibold" style={{ fontSize: '13px', color: '#1e293b' }}>
          {node.title}
        </Popover.Header>
        <Popover.Body className="p-2" style={{ minWidth: '180px', maxHeight: '350px', overflowY: 'auto' }}>
          <ul className="list-unstyled mb-0">
            {popoverContent(node.children)}
          </ul>
        </Popover.Body>
      </Popover>
    );

    return (
      <li className="sidebar-item">
        <OverlayTrigger
          trigger="click"
          rootClose
          placement="right"
          overlay={popover}
        >
          <div className="w-100">
            <Button
              type="button"
              className={`sidebar-btn d-flex align-items-center justify-content-center w-100 ${isActive ? "active" : ""}`}
              style={{ paddingLeft: "12px" }}
            >

              {node.icon && <span className="sidebar-icon-wrapper d-inline-flex">{node.icon}</span>}

            </Button>
          </div>
        </OverlayTrigger>
      </li>
    );
  }

  return (
    <li className="sidebar-item">
      <Button
        type="button"
        onClick={() => onToggle(level, node.title)}
        className={`sidebar-btn w-100 ${isActive ? "active" : ""}`}
        style={{ paddingLeft: isCollapsed ? "12px" : padLeft }}
        aria-expanded={isOpen}
      >

        {node.icon && <span className="sidebar-icon-wrapper d-inline-flex">{node.icon}</span>}
        {!isCollapsed && <span className="text-start text-truncate">{node.title}</span>}

        {!isCollapsed && (
          <ChevronRight size={14} className={`chevron-icon ms-auto rotate-icon ${isOpen ? 'rotate' : ''}`} />
        )}
      </Button>

      <Collapse in={isOpen}>
        <ul className="list-unstyled">
          {node.children.map((child, idx) => {
            const isChildOpen = openPath[level + 1] === child.title;
            return (
              <SidebarNode
                key={idx}
                node={child}
                level={level + 1}
                locationPath={locationPath}
                isOpen={isChildOpen}
                onToggle={onToggle}
                openPath={openPath}
                userDetail={userDetail}
                fromPath={fromPath}
                isCollapsed={isCollapsed}
              />
            );
          })}
        </ul>
      </Collapse>
    </li>
  );
}

const Sidebar = ({ isCollapsed, setIsCollapsed }) => {
  const location = useLocation();
  const mapTypes = useSelector((state) => state.maps.list);
  const jantriTypes = useSelector((state) => state.jantri.list);
  const userDetail = useSelector((state) => state.allUsers.userDetail);

  const mapChildren = useMemo(() => {
    return mapTypes.map((type) => ({
      title: type.code.toUpperCase(), // TP / DP / VILLAGE / GDCR
      icon: <RecordIcon size={10} />,
      link: `/map/${type.code}`, // OR routes[type.code]
    }));
  }, [mapTypes]);

  const jantriChildren = useMemo(() => {
    return jantriTypes.map((type) => ({
      title: type.code?.toUpperCase() || type.name,
      icon: <RecordIcon size={10} />,
      link: `/jantri/${type.code}`,
    }));
  }, [jantriTypes]);

  const { hasPermission } = usePermission();
  const { setLoading } = useLoader();
  const hasMounted = useMounted();
  const navigate = useNavigate();
  const user = localStorage.getItem("userData");
  let userData = null;
  if (user) {
    userData = JSON.parse(user);
  }

  const getUserInitial = (name) => {
    if (!name) return "UN";
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };

  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const handleLogOut = () => {
    setShowLogoutModal(true);
  };

  const confirmLogOut = async () => {
    setShowLogoutModal(false);
    setLoading(true);
    localStorage.removeItem("auth-token");
    localStorage.removeItem("userData");
    setTimeout(() => {
      if (hasMounted) {
        navigate("/login", { replace: true });
        setLoading(false);
      }
    }, 1000);
  };

  const finalSidebarContent = useMemo(() => {
    const filterNodes = (nodes, parentModule = null) => {
      return nodes
        .map((node) => {
          let updatedNode = { ...node };
          const currentModule = updatedNode.module || parentModule;

          // Handle dynamic children first
          if (node.key === "MAP_DYNAMIC") {
            updatedNode.children = mapChildren;
          } else if (node.key === "JANTRI_DYNAMIC") {
            updatedNode.children = jantriChildren;
          }

          // Recursively filter children if any, passing down the current module
          if (updatedNode.children && updatedNode.children.length > 0) {
            updatedNode.children = filterNodes(updatedNode.children, currentModule);
          }

          // Visibility logic:
          // 1. If it has a module (own or inherited), check for 'read' permission
          // 2. If it's a group (to: "#"), only show if it has visible children
          // 3. If it has no module and no children (leaf), it's always visible

          const hasModuleAccess = !currentModule || hasPermission(currentModule, "read");
          const isLeaf = updatedNode.link && updatedNode.link !== "#";
          const hasVisibleChildren = updatedNode.children && updatedNode.children.length > 0;

          if (hasModuleAccess && (isLeaf || hasVisibleChildren)) {
            return updatedNode;
          }
          return null;
        })
        .filter(Boolean);
    };

    return filterNodes(SidebarContent);
  }, [mapChildren, jantriChildren, hasPermission]);

  // --- Accordion Logic ---
  // openPath is an array of titles, e.g. ["NPA", "Bank Manage"]
  const [openPath, setOpenPath] = useState([]);

  // Initialize openPath based on current URL on mount
  useEffect(() => {
    const path = findActivePath(finalSidebarContent, location.pathname, location.state?.from, userDetail);
    if (path) {
      setOpenPath(path);
    }
  }, [location.pathname, finalSidebarContent, location.state, userDetail]);

  const handleToggle = (level, title) => {
    setOpenPath((prevPath) => {
      // If the clicked item is already open at this level, close it (and all deeper levels)
      if (prevPath[level] === title) {
        return prevPath.slice(0, level);
      }
      // Otherwise, open this item, closing any siblings at the same level (inherent in replacing index 'level')
      // and discarding any deeper levels from the *previous* path.
      return [...prevPath.slice(0, level), title];
    });
  };

  // Group items by category to match visual design
  const categorizedSidebarContent = useMemo(() => {
    const categories = {
      MANAGE: [],
      ADMIN: []
    };

    finalSidebarContent.forEach((node) => {
      const title = node.title;
      if (["User", "Company", "Subscription", "Jantri", "Map", "Banner", "NPA", "Public Properties", "Public Lands", "Broker CRM", "Marketplace", "Intro Screens", "Profile Suggestions"].includes(title)) {
        categories.MANAGE.push(node);
      } else if (["Setting", "Admin Management"].includes(title)) {
        categories.ADMIN.push(node);
      } else {
        categories.MANAGE.push(node);
      }
    });

    return categories;
  }, [finalSidebarContent]);

  // const isDashboardActive = location.pathname === "/" || location.pathname === "/dashboard";

  return (
    <>
      <aside className="bg-white position-fixed start-0 top-0 bottom-0 sidebar border-end">
        <div className="h-100 position-relative d-flex flex-column">
          {/* Sidebar Header with Logo and Collapse Toggle */}
          <div className="site-logo d-flex align-items-center justify-content-between px-3">
            <Link to={routes.userRPoint} className="d-flex align-items-center logo-link text-decoration-none">
              <Image
                src={isCollapsed ? "/logo-sm.png" : "/logo-main.png"}
                alt="logo"
                height="36"
                className="logo-img"
                style={{ objectFit: 'contain', width: isCollapsed ? '32px' : 'auto' }}
              />
            </Link>
            <button
              type="button"
              className="sidebar-toggle-btn border d-flex align-items-center justify-content-center rounded-circle"
              onClick={() => setIsCollapsed(!isCollapsed)}
              aria-label="Toggle Sidebar"
            >
              {isCollapsed ? <ChevronsRight size={14} /> : <ChevronsLeft size={14} />}
            </button>
          </div>

          {/* Sidebar Items */}
          <div className="py-2 px-2 flex-grow-1 overflow-y-auto sidebar-scrollable-content">
            <ul className="mb-0 list-unstyled">
              {/* Dashboard Link (Always at the top) */}
              {/* <li className="sidebar-item mb-1">
                <Link
                to={routes.userRPoint}
                className={`sidebar-link dashboard-link ${isDashboardActive ? "active" : ""
                  }`}
                style={{ paddingLeft: "12px" }}
                >
                  <span className="sidebar-icon-wrapper d-inline-flex">
                    <DashboardSquare02Icon size={18} />
                  </span>
                  {!isCollapsed && <span className="sidebar-title-mr font-medium">Dashboard</span>}

                </Link>
              </li> */}

              {/* Categorized Menu Section */}
              {Object.entries(categorizedSidebarContent).map(([category, nodes]) => {
                if (nodes.length === 0) return null;
                return (
                  <React.Fragment key={category}>
                    {!isCollapsed && (
                      <li className="sidebar-category-header text-uppercase">
                        {category}
                      </li>
                    )}
                    {nodes.map((node, i) => {
                      const isOpen = openPath[0] === node.title;
                      return (
                        <SidebarNode
                          key={i}
                          node={node}
                          level={0}
                          locationPath={location.pathname}
                          isOpen={isOpen}
                          onToggle={handleToggle}
                          openPath={openPath}
                          userDetail={userDetail}
                          fromPath={location.state?.from}
                          isCollapsed={isCollapsed}
                        />
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </ul>
          </div>

          {/* Profile Section */}
          <div className="px-3 py-2 mt-auto border-top">
            {isCollapsed ? (
              <div className="d-flex flex-column align-items-center gap-2 py-2">
                <div className="rounded-circle d-flex align-items-center justify-content-center profile-icon" style={{ width: '40px', height: '40px', backgroundColor: '#e9ecef', color: '#444050' }}>
                  {getUserInitial(userData?.data?.admin?.name)}
                </div>
                <button
                  type="button"
                  className="btn btn-primary rounded-circle d-flex align-items-center justify-content-center p-0"
                  style={{ width: '24px', height: '24px' }}
                  onClick={() => setIsCollapsed(false)}
                  aria-label="Expand Sidebar"
                >
                  <ChevronsRight size={16} />
                </button>
              </div>
            ) : (
              <div className="position-relative p-0 profile-toggle w-100 text-start text-decoration-none d-flex align-items-center gap-2">
                <div className="rounded-circle d-flex align-items-center justify-content-center profile-icon" style={{ width: '40px', height: '40px', backgroundColor: '#e9ecef', color: '#444050' }}>
                  {getUserInitial(userData?.data?.admin?.name)}
                </div>
                <div className="overflow-hidden flex-grow-1 text-dark">
                  <h6 className="fw-medium text-capitalize mb-0 text-truncate" style={{ fontSize: '14px' }}>
                    {userData?.data?.admin?.name || 'Admin'}
                  </h6>
                </div>
                <button className="btn btn-primary rounded-circle d-flex align-items-center justify-content-center p-0" style={{ width: '40px', height: '40px' }} onClick={handleLogOut}>
                  <LogoutIcon size={20} />
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>
      <ConfirmModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        title="Logout"
        message="Are you sure you want to logout?"
        onConfirm={confirmLogOut}
        confirmText="Yes, Logout"
        cancelText="Cancel"
        size="sm"
      />
    </>
  );
};

export default Sidebar;